import { authFetch } from '../utils/authFetch';

interface TokenPayload {
  token?: string;
}

interface FetchTokenOptions {
  autoLogout?: boolean;
  signal?: AbortSignal;
}

export interface RealtimeTokenResult {
  token: string | null;
  status: number | null;
}

export interface ReceptionBackfillEvent {
  idx?: number;
  event?: string;
  event_type?: string;
  event_id?: string;
  [key: string]: unknown;
}

export interface ReceptionBackfillResponse {
  events: ReceptionBackfillEvent[];
  last: number;
}

const joinUrl = (endpoint: string, query: URLSearchParams): string => {
  const suffix = query.toString();
  if (!suffix) return endpoint;
  return endpoint.includes('?') ? `${endpoint}&${suffix}` : `${endpoint}?${suffix}`;
};

export async function fetchRealtimeToken(
  endpoint: string,
  options: FetchTokenOptions = {},
): Promise<string | null> {
  const result = await fetchRealtimeTokenResult(endpoint, options);
  return result.token;
}

export async function fetchRealtimeTokenResult(
  endpoint: string,
  options: FetchTokenOptions = {},
): Promise<RealtimeTokenResult> {
  const response = await authFetch(endpoint, {
    autoLogout: options.autoLogout ?? false,
    signal: options.signal,
  });
  if (!response.ok) {
    return { token: null, status: response.status };
  }

  const payload = (await response.json()) as TokenPayload;
  const token = typeof payload.token === 'string' && payload.token.length > 0 ? payload.token : null;
  return { token, status: response.status };
}

export async function fetchReceptionWsToken(options: FetchTokenOptions = {}): Promise<string | null> {
  return fetchRealtimeToken('/api/ws/token?channel=reception', options);
}

export async function fetchReceptionEventsSince(
  since: number,
  options: FetchTokenOptions = {},
): Promise<ReceptionBackfillResponse | null> {
  const cursor = Number.isFinite(since) ? Math.trunc(since) : -1;
  const response = await authFetch(`/reception/events?since=${encodeURIComponent(String(cursor))}`, {
    autoLogout: options.autoLogout ?? false,
    signal: options.signal,
    skipDedupe: true,
  });
  if (!response.ok) return null;
  const payload = (await response.json()) as Partial<ReceptionBackfillResponse>;
  return {
    events: Array.isArray(payload.events) ? payload.events : [],
    last: Number.isFinite(Number(payload.last)) ? Number(payload.last) : cursor,
  };
}

export async function fetchReceptionDailyReportsWsToken(
  options: FetchTokenOptions = {},
): Promise<string | null> {
  return fetchRealtimeToken('/reception/ws/daily-reports/token', options);
}

export async function fetchStudentWsToken(options: FetchTokenOptions = {}): Promise<string | null> {
  return fetchRealtimeToken('/student/ws/token', options);
}

export async function fetchReceptionChatToken(options: {
  endpoint?: string;
  meta?: boolean;
  studentId?: number;
  autoLogout?: boolean;
  signal?: AbortSignal;
} = {}): Promise<string | null> {
  const query = new URLSearchParams();
  if (options.meta) {
    query.set('meta', '1');
  }
  if (typeof options.studentId === 'number' && Number.isFinite(options.studentId)) {
    query.set('student_id', String(options.studentId));
  }

  const endpoint = joinUrl(options.endpoint ?? '/reception/ws/chat-token', query);
  return fetchRealtimeToken(endpoint, {
    autoLogout: options.autoLogout ?? false,
    signal: options.signal,
  });
}
